'use client';

import { useState, useEffect } from 'react';
import moment from 'moment';

import BillsTableHeader from './Table-Header';

import CompactSidebarFilter from '../../../utils/Compact-Sidebar-Filter';
import EditModal from '../../../utils/Compact-Edit_modal';
import SmartPagination from '../../../utils/Smart-Pagination';

const EnhancedBillsSection = ({
  SIDEBAR_OPTIONS,
  dropdownOptions,
  total_bills,
  total_amount_paid,
  total_amount_not_paid,
  bills_loading,
  bills,
  page,
  pages,
  setPageNumber,
  LIMIT,
  updateStatus,
  deleteBtnClicked,
  setModal,
  setModalData,
  loadingActivityStatus,
  reloadData,
  loadOptions,
  LINK_URL,
  PAGE_SINGLE_TITLE,
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Load saved sidebar state
  useEffect(() => {
    const savedSidebarState = localStorage.getItem('bills-sidebar-collapsed');
    if (savedSidebarState) {
      setSidebarCollapsed(JSON.parse(savedSidebarState));
    }
  }, []);

  // Save sidebar state to localStorage
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('bills-sidebar-collapsed', JSON.stringify(newState));
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      PAID: 'badge bg-success',
      'NOT PAID': 'badge bg-danger',
      CANCELLED: 'badge bg-secondary',
      PENDING: 'badge bg-warning text-dark',
    };
    return statusClasses[status] || 'badge bg-light text-dark';
  };

  const handleEdit = (billId, field, currentValue, fieldLabel, single) => {
    setEditData({ billId, field, currentValue, fieldLabel, single });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (billId, field, value) => {
    const newValues = { [field]: value };
    await updateStatus(billId, newValues);
    setShowEditModal(false);
    setEditData(null);
  };

  const isOverdue = (single) => {
    return (
      single.status &&
      single.status !== 'PAID' &&
      single.status !== 'CANCELLED' &&
      moment().diff(moment(single.billing_date)) > 0
    );
  };

  return (
    <div className='container-fluid'>
      <div className='row'>
        {/* Collapsible Sidebar */}
        {SIDEBAR_OPTIONS && (
          <div
            className={`${
              sidebarCollapsed ? 'd-none' : 'col-xl-2 col-lg-3'
            } transition-all`}
            style={{
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <CompactSidebarFilter
              SIDEBAR_OPTIONS={SIDEBAR_OPTIONS}
              dropdown_options={dropdownOptions}
              loadOptions={loadOptions}
              // onToggle={toggleSidebar}
              isCollapsed={sidebarCollapsed}
            />
          </div>
        )}

        {/* Main Bills Section */}
        <div
          className={`${
            sidebarCollapsed ? 'col-lg-12' : 'col-xl-10 col-lg-9'
          } transition-all`}
        >
          <div className='card shadow-sm border-0'>
            {/* Bills Header with Sidebar Toggle */}
            <div className='card-header bg-white border-bottom py-3'>
              <div className='d-flex align-items-center justify-content-between'>
                <div className='d-flex align-items-center'>
                  {/* Sidebar Toggle Button */}
                  <button
                    className='btn btn-outline-secondary btn-sm me-3'
                    onClick={toggleSidebar}
                    title={sidebarCollapsed ? 'Show Filters' : 'Hide Filters'}
                    style={{
                      fontSize: '12px',
                      padding: '6px 10px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <i
                      className={`fa ${
                        sidebarCollapsed ? 'fa-filter' : 'fa-times'
                      } me-1`}
                      style={{ fontSize: '11px' }}
                    ></i>
                    {sidebarCollapsed ? 'Show Filters' : 'Hide Filters'}
                  </button>
                  <h5 className='mb-0 text-dark'>Bills Management</h5>
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <a
                    className='btn btn-danger btn-sm'
                    href={`/bills?conditional[billing_date][$lt]=${moment()
                      .utc()
                      .format('YYYY-MM-DD')}&exact[status]=NOT PAID`}
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    <i
                      className='fa fa-exclamation-triangle me-1'
                      style={{ fontSize: '11px' }}
                    ></i>
                    Overdue Bills
                  </a>
                  <a
                    className='btn btn-dark btn-sm'
                    href={LINK_URL}
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    <i
                      className='fa fa-plus me-1'
                      style={{ fontSize: '11px' }}
                    ></i>
                    Add {PAGE_SINGLE_TITLE}
                  </a>
                </div>
              </div>
            </div>

            {/* Bills Summary Header */}
            <BillsTableHeader
              total_bills={total_bills}
              total_amount_paid={total_amount_paid}
              total_amount_not_paid={total_amount_not_paid}
              sidebarCollapsed={sidebarCollapsed}
            />

            <div className='card-body pt-0'>
              {/* Records Count */}
              <div className='d-flex justify-content-between align-items-center mb-3 mt-3'>
                <div
                  className='text-muted'
                  style={{ fontSize: '12px', fontWeight: '500' }}
                >
                  <i
                    className='fa fa-list me-2'
                    style={{ fontSize: '11px' }}
                  ></i>
                  {total_bills} records found
                  {sidebarCollapsed && (
                    <span
                      className='badge bg-info ms-2'
                      style={{ fontSize: '10px' }}
                    >
                      <i className='fa fa-expand-arrows-alt me-1'></i>
                      Full Width
                    </span>
                  )}
                </div>
              </div>

              {/* Enhanced Bills Table */}
              <div
                className='table-responsive'
                style={{
                  position: 'relative',
                  overflow: 'auto',
                  maxHeight: '75vh',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                }}
              >
                <table
                  className='table table-hover align-middle'
                  style={{
                    tableLayout: 'fixed',
                    minWidth: '1600px',
                    border: '1px solid #e9ecef',
                  }}
                >
                  {!bills_loading && (
                    <thead>
                      <tr
                        className='table-light'
                        style={{ borderBottom: '2px solid #dee2e6' }}
                      >
                        <th
                          className='border-end py-3 text-center'
                          style={{
                            width: '60px',
                            fontSize: '11px',
                            fontWeight: '600',
                            borderRight: '1px solid #e9ecef',
                          }}
                        >
                          <span className='text-muted'>#</span>
                        </th>
                        <th
                          className='border-end py-3'
                          style={{
                            width: '160px',
                            fontSize: '11px',
                            fontWeight: '600',
                            borderRight: '1px solid #e9ecef',
                          }}
                        >
                          <span className='text-muted'>CLIENT</span>
                        </th>
                        <th
                          className='border-end py-3'
                          style={{
                            width: '180px',
                            fontSize: '11px',
                            fontWeight: '600',
                            borderRight: '1px solid #e9ecef',
                          }}
                        >
                          <span className='text-muted'>BILLING DATE</span>
                        </th>
                        <th
                          className='border-end py-3'
                          style={{
                            width: '160px',
                            fontSize: '11px',
                            fontWeight: '600',
                            borderRight: '1px solid #e9ecef',
                          }}
                        >
                          <span className='text-muted'>COLLECTION DATE</span>
                        </th>
                        <th
                          className='border-end py-3 text-center'
                          style={{
                            width: '120px',
                            fontSize: '11px',
                            fontWeight: '600',
                            borderRight: '1px solid #e9ecef',
                          }}
                        >
                          <span className='text-muted'>AMOUNT</span>
                        </th>
                        <th
                          className='border-end py-3'
                          style={{
                            width: '120px',
                            fontSize: '11px',
                            fontWeight: '600',
                            borderRight: '1px solid #e9ecef',
                          }}
                        >
                          <span className='text-muted'>STATUS</span>
                        </th>
                        <th
                          className='border-end py-3'
                          style={{
                            width: '140px',
                            fontSize: '11px',
                            fontWeight: '600',
                            borderRight: '1px solid #e9ecef',
                          }}
                        >
                          <span className='text-muted'>REVIEW</span>
                        </th>
                        <th
                          className='border-end py-3'
                          style={{
                            width: '140px',
                            fontSize: '11px',
                            fontWeight: '600',
                            borderRight: '1px solid #e9ecef',
                          }}
                        >
                          <span className='text-muted'>PACKAGE</span>
                        </th>
                        <th
                          className='border-end py-3'
                          style={{
                            width: '140px',
                            fontSize: '11px',
                            fontWeight: '600',
                            borderRight: '1px solid #e9ecef',
                          }}
                        >
                          <span className='text-muted'>COLLECTED BY</span>
                        </th>
                        <th
                          className='border-end py-3'
                          style={{
                            width: '140px',
                            fontSize: '11px',
                            fontWeight: '600',
                            borderRight: '1px solid #e9ecef',
                          }}
                        >
                          <span className='text-muted'>BANK ACCOUNT</span>
                        </th>
                        <th
                          className='py-3 text-center'
                          style={{
                            position: 'sticky',
                            right: 0,
                            backgroundColor: '#f8f9fa',
                            borderLeft: '1px solid #dee2e6',
                            boxShadow: '-2px 0 4px rgba(0,0,0,0.05)',
                            zIndex: 5,
                            width: '100px',
                            minWidth: '100px',
                            fontSize: '11px',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span className='text-muted'>ACTIONS</span>
                        </th>
                      </tr>
                    </thead>
                  )}

                  <tbody>
                    {!bills_loading ? (
                      bills &&
                      bills.map((single, index) => {
                        const overdueStatus = isOverdue(single);

                        return (
                          <tr
                            key={single._id}
                            className={
                              overdueStatus
                                ? 'table-danger table-danger-subtle'
                                : ''
                            }
                            style={{ borderBottom: '1px solid #f8f9fa' }}
                          >
                            {/* Serial Number */}
                            <td
                              className='border-end py-3 text-center'
                              style={{
                                borderRight: '1px solid #f8f9fa',
                                width: '60px',
                              }}
                            >
                              <span
                                className='badge bg-light text-dark'
                                style={{
                                  fontSize: '11px',
                                  fontWeight: '600',
                                }}
                              >
                                {index + 1 + (page - 1) * LIMIT}
                              </span>
                            </td>

                            {/* Client */}
                            <td
                              className='border-end py-3'
                              style={{
                                width: '160px',
                                borderRight: '1px solid #f8f9fa',
                                maxWidth: '160px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <div className='d-flex align-items-center justify-content-between'>
                                <div
                                  className='text-truncate me-2'
                                  title={single.client?.name}
                                >
                                  <span
                                    style={{
                                      fontSize: '13px',
                                      fontWeight: '500',
                                      color: '#212529',
                                    }}
                                  >
                                    {single.client?.name}
                                  </span>
                                </div>
                                <button
                                  className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                  onClick={() =>
                                    handleEdit(
                                      single._id,
                                      'client',
                                      single.client?._id,
                                      'Client',
                                      single
                                    )
                                  }
                                  title='Edit Client'
                                  style={{ fontSize: '11px' }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            </td>

                            {/* Billing Date */}
                            <td
                              className='border-end py-3'
                              style={{
                                width: '180px',
                                borderRight: '1px solid #f8f9fa',
                              }}
                            >
                              <div className='d-flex align-items-center justify-content-between'>
                                <div>
                                  <div
                                    style={{
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      color: '#212529',
                                    }}
                                  >
                                    {single.billing_date &&
                                      moment(single.billing_date)
                                        .utc()
                                        .format('DD-MM-YYYY')}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '11px',
                                      color: '#6c757d',
                                    }}
                                  >
                                    {single.billing_date &&
                                      moment(single.billing_date)
                                        .utc()
                                        .format('hh:mm A')}
                                  </div>
                                  {overdueStatus && (
                                    <div className='mt-1'>
                                      <span
                                        className='badge bg-danger'
                                        style={{ fontSize: '10px' }}
                                      >
                                        <i
                                          className='fa fa-exclamation-triangle me-1'
                                          style={{ fontSize: '9px' }}
                                        ></i>
                                        Overdue
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <button
                                  className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                  onClick={() =>
                                    handleEdit(
                                      single._id,
                                      'billing_date',
                                      moment(single.billing_date).format(
                                        'YYYY-MM-DDTHH:mm'
                                      ),
                                      'Billing Date',
                                      single
                                    )
                                  }
                                  title='Edit Billing Date'
                                  style={{ fontSize: '11px' }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            </td>

                            {/* Collection Date */}
                            <td
                              className='border-end py-3'
                              style={{
                                width: '160px',
                                borderRight: '1px solid #f8f9fa',
                              }}
                            >
                              <div className='d-flex align-items-center justify-content-between'>
                                <div>
                                  <span
                                    style={{
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      color: '#212529',
                                    }}
                                  >
                                    {single.collection_date &&
                                      moment(single.collection_date).format(
                                        'DD-MM-YYYY'
                                      )}
                                  </span>
                                </div>
                                <button
                                  className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                  onClick={() =>
                                    handleEdit(
                                      single._id,
                                      'collection_date',
                                      moment(single.collection_date).format(
                                        'YYYY-MM-DDTHH:mm'
                                      ),
                                      'Collection Date',
                                      single
                                    )
                                  }
                                  title='Edit Collection Date'
                                  style={{ fontSize: '11px' }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            </td>

                            {/* Amount */}
                            <td
                              className='border-end py-3 text-center'
                              style={{
                                width: '120px',
                                borderRight: '1px solid #f8f9fa',
                              }}
                            >
                              <div className='d-flex align-items-center justify-content-center'>
                                <span
                                  className='badge bg-light text-dark me-2'
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: '500',
                                  }}
                                >
                                  ₹{single.amount?.toLocaleString('en-IN')}
                                </span>
                                <button
                                  className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                  onClick={() =>
                                    handleEdit(
                                      single._id,
                                      'amount',
                                      single.amount,
                                      'Amount',
                                      single
                                    )
                                  }
                                  title='Edit Amount'
                                  style={{ fontSize: '11px' }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            </td>

                            {/* Status */}
                            <td
                              className='border-end py-3'
                              style={{
                                width: '120px',
                                borderRight: '1px solid #f8f9fa',
                              }}
                            >
                              <div className='d-flex align-items-center justify-content-between'>
                                <span
                                  className={getStatusBadge(single.status)}
                                  style={{
                                    fontSize: '10px',
                                    fontWeight: '500',
                                  }}
                                >
                                  {single.status}
                                </span>
                                <button
                                  className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                  onClick={() =>
                                    handleEdit(
                                      single._id,
                                      'status',
                                      single.status,
                                      'Status',
                                      single
                                    )
                                  }
                                  title='Edit Status'
                                  style={{ fontSize: '11px' }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            </td>

                            {/* Review Status */}
                            <td
                              className='border-end py-3'
                              style={{
                                width: '140px',
                                borderRight: '1px solid #f8f9fa',
                              }}
                            >
                              <div className='d-flex align-items-center justify-content-between'>
                                <span
                                  className='badge bg-light text-dark'
                                  style={{
                                    fontSize: '10px',
                                    fontWeight: '500',
                                  }}
                                >
                                  {single.review_status?.name}
                                </span>
                                <button
                                  className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                  onClick={() =>
                                    handleEdit(
                                      single._id,
                                      'review_status',
                                      single.review_status?._id,
                                      'Review Status',
                                      single
                                    )
                                  }
                                  title='Edit Review Status'
                                  style={{ fontSize: '11px' }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            </td>

                            {/* Package */}
                            <td
                              className='border-end py-3'
                              style={{
                                width: '140px',
                                borderRight: '1px solid #f8f9fa',
                                maxWidth: '140px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <div className='d-flex align-items-center justify-content-between'>
                                <div
                                  className='text-truncate me-2'
                                  title={single.package?.name}
                                >
                                  <span
                                    style={{
                                      fontSize: '13px',
                                      fontWeight: '500',
                                      color: '#495057',
                                    }}
                                  >
                                    {single.package?.name}
                                  </span>
                                </div>
                                <button
                                  className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                  onClick={() =>
                                    handleEdit(
                                      single._id,
                                      'package',
                                      single.package?._id,
                                      'Package',
                                      single
                                    )
                                  }
                                  title='Edit Package'
                                  style={{ fontSize: '11px' }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            </td>

                            {/* Collected By */}
                            <td
                              className='border-end py-3'
                              style={{
                                width: '140px',
                                borderRight: '1px solid #f8f9fa',
                                maxWidth: '140px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <div className='d-flex align-items-center justify-content-between'>
                                <div
                                  className='text-truncate me-2'
                                  title={single.collected_by?.name}
                                >
                                  <span
                                    style={{
                                      fontSize: '13px',
                                      fontWeight: '500',
                                      color: '#495057',
                                    }}
                                  >
                                    {single.collected_by?.name}
                                  </span>
                                </div>
                                <button
                                  className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                  onClick={() =>
                                    handleEdit(
                                      single._id,
                                      'collected_by',
                                      single.collected_by?._id,
                                      'Collected By',
                                      single
                                    )
                                  }
                                  title='Edit Collected By'
                                  style={{ fontSize: '11px' }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            </td>

                            {/* Bank Account */}
                            <td
                              className='border-end py-3'
                              style={{
                                width: '140px',
                                borderRight: '1px solid #f8f9fa',
                                maxWidth: '140px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <div className='d-flex align-items-center justify-content-between'>
                                <div
                                  className='text-truncate me-2'
                                  title={single.bank_account?.name}
                                >
                                  <span
                                    style={{
                                      fontSize: '13px',
                                      fontWeight: '500',
                                      color: '#495057',
                                    }}
                                  >
                                    {single.bank_account?.name}
                                  </span>
                                </div>
                                <button
                                  className='btn btn-link btn-sm p-1 text-muted flex-shrink-0'
                                  onClick={() =>
                                    handleEdit(
                                      single._id,
                                      'bank_account',
                                      single.bank_account?._id,
                                      'Bank Account',
                                      single
                                    )
                                  }
                                  title='Edit Bank Account'
                                  style={{ fontSize: '11px' }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            </td>

                            {/* Actions */}
                            <td
                              className='py-3 text-center'
                              style={{
                                position: 'sticky',
                                right: 0,
                                backgroundColor: '#ffffff',
                                borderLeft: '1px solid #dee2e6',
                                boxShadow: '-2px 0 4px rgba(0,0,0,0.05)',
                                zIndex: 5,
                                width: '100px',
                                minWidth: '100px',
                              }}
                            >
                              <div className='d-flex justify-content-center align-items-center gap-1'>
                                <button
                                  className='btn btn-outline-info btn-sm'
                                  onClick={() => {
                                    setModal(true);
                                    setModalData(single);
                                  }}
                                  title='View Details'
                                  style={{
                                    fontSize: '4px',
                                    padding: '4px 6px',
                                    minWidth: '28px',
                                    height: '24px',
                                    borderRadius: '4px',
                                  }}
                                >
                                  <i
                                    className='fa fa-binoculars'
                                    style={{ fontSize: '10px' }}
                                  ></i>
                                </button>
                                <button
                                  className='btn btn-outline-danger btn-sm'
                                  onClick={() => deleteBtnClicked(single._id)}
                                  title='Delete'
                                  style={{
                                    fontSize: '4px',
                                    padding: '4px 6px',
                                    minWidth: '28px',
                                    height: '24px',
                                    borderRadius: '4px',
                                  }}
                                >
                                  <i
                                    className='fa fa-trash'
                                    style={{ fontSize: '10px' }}
                                  ></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={11} className='text-center py-5'>
                          <div className='d-flex flex-column align-items-center'>
                            <div
                              className='spinner-border text-primary mb-3'
                              role='status'
                            >
                              <span className='visually-hidden'>
                                Loading...
                              </span>
                            </div>
                            <div
                              className='text-muted'
                              style={{ fontSize: '13px' }}
                            >
                              Loading bills...
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Enhanced Pagination */}

              <SmartPagination
                page={page}
                pages={pages}
                total_count={total_bills}
                setPageNumber={setPageNumber}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <EditModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditData(null);
        }}
        editData={editData}
        dropdownOptions={dropdownOptions}
        onSave={handleSaveEdit}
        loading={loadingActivityStatus}
      />
    </div>
  );
};

export default EnhancedBillsSection;
